// LoginPage.js — Login page for Management Portal
import { useState } from 'react';
import { msalInstance } from '../msalInstance';
import { loginRequest } from '../authConfig';
import api from '../services/api';

// Lab-and-cloud illustration for the left panel (a stand-in until real photography is chosen)
function LoginArt() {
  const cloud = (x, y, k, o) => (
    <g transform={`translate(${x} ${y}) scale(${k})`} fill="#fff" opacity={o}>
      <circle cx="40" cy="50" r="26" /><circle cx="80" cy="34" r="34" /><circle cx="124" cy="50" r="28" /><rect x="14" y="48" width="140" height="28" rx="14" />
    </g>
  );
  return (
    <svg viewBox="0 0 600 440" preserveAspectRatio="xMidYMax meet" style={s.art} aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="liquidBlue" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#4FC3F7" /><stop offset="1" stopColor="#009BDD" /></linearGradient>
        <linearGradient id="liquidViolet" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#B39DDB" /><stop offset="1" stopColor="#7E57C2" /></linearGradient>
      </defs>
      {cloud(300, 40, 1.5, 0.10)}{cloud(20, 120, 1, 0.08)}{cloud(380, 150, 1.0, 0.12)}
      {/* molecule */}
      <g stroke="#fff" strokeOpacity="0.35" strokeWidth="2" fill="none"><line x1="470" y1="70" x2="520" y2="100" /><line x1="520" y1="100" x2="560" y2="60" /><line x1="520" y1="100" x2="525" y2="155" /><line x1="470" y1="70" x2="440" y2="115" /></g>
      <g fill="#fff"><circle cx="470" cy="70" r="9" opacity="0.5" /><circle cx="520" cy="100" r="13" opacity="0.6" /><circle cx="560" cy="60" r="7" opacity="0.4" /><circle cx="525" cy="155" r="8" opacity="0.4" /><circle cx="440" cy="115" r="7" opacity="0.4" /></g>
      {/* conical flask */}
      <g>
        <path d="M232 150 h36 v62 l56 104 q10 22 -12 22 h-124 q-22 0 -12 -22 l56 -104 z" fill="#fff" fillOpacity="0.10" stroke="#fff" strokeOpacity="0.8" strokeWidth="3" strokeLinejoin="round" />
        <path d="M205 296 l25 -46 h40 l25 46 l17 32 q5 12 -8 12 h-108 q-13 0 -8 -12 z" fill="url(#liquidBlue)" opacity="0.95" />
        <rect x="224" y="140" width="52" height="12" rx="4" fill="#fff" fillOpacity="0.85" />
        <circle cx="245" cy="290" r="5" fill="#fff" opacity="0.8" /><circle cx="270" cy="304" r="3.5" fill="#fff" opacity="0.7" /><circle cx="258" cy="270" r="3" fill="#fff" opacity="0.7" />
      </g>
      {/* test tubes in a rack */}
      <g>
        {[[392, '#liquidBlue', 120], [434, '#liquidViolet', 90], [476, '#liquidBlue', 140]].map(([x, fill, level], i) => (
          <g key={i}>
            <path d={`M${x} 232 h26 v118 a13 13 0 0 1 -26 0 z`} fill="#fff" fillOpacity="0.10" stroke="#fff" strokeOpacity="0.8" strokeWidth="3" />
            <path d={`M${x + 1.5} ${350 - level + 40} h23 v${level - 40 - 4 + 4} a11.5 11.5 0 0 1 -23 0 z`} fill={`url(${fill})`} />
          </g>
        ))}
        <rect x="378" y="296" width="140" height="10" rx="5" fill="#fff" fillOpacity="0.55" />
        <rect x="372" y="364" width="152" height="12" rx="6" fill="#fff" fillOpacity="0.45" />
      </g>
      {/* bench */}
      <rect x="40" y="376" width="520" height="8" rx="4" fill="#fff" fillOpacity="0.35" />
      {/* data line */}
      <polyline points="60,420 130,400 190,412 260,392 330,404 400,386 470,398 540,380" fill="none" stroke="#4FC3F7" strokeOpacity="0.7" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function LoginPage({ onOtpToken }) {
  const [email,   setEmail]   = useState('');
  const [step,    setStep]    = useState('email');
  const [code,    setCode]    = useState('');
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);

  const isStarLIMS = email.toLowerCase().trim().endsWith('@starlims.com');

  const handleEmailSubmit = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setError('');
    setLoading(true);
    if (isStarLIMS) {
      msalInstance.loginRedirect(loginRequest);
      return;
    }
    try {
      await api.post('/auth/request-otp', { email: trimmed });
      setSent(true);
      setStep('otp');
    } catch {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleOtpSubmit = async () => {
    if (code.length !== 6) { setError('Please enter the 6-digit code.'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/verify-otp', { email: email.trim(), code: code.trim() });
      localStorage.setItem('otp_token', res.data.token);
      onOtpToken(res.data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid or expired code. Please try again.');
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError(''); setCode(''); setSent(false); setLoading(true);
    try {
      await api.post('/auth/request-otp', { email: email.trim() });
      setSent(true);
    } catch {
      setError('Could not resend. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div style={s.shell}>
      {/* ── Left brand panel ── */}
      <div style={s.left}>
        <div style={s.leftInner}>
          <div style={s.logoTile}>
            <img src={`${process.env.PUBLIC_URL}/starlims-logo.png`} alt="STARLIMS" style={s.logoTileImg} />
          </div>
        </div>
        <LoginArt />
        <div style={s.leftFooter}>
          &copy; {new Date().getFullYear()} StarLIMS. All rights reserved.
        </div>
      </div>

      {/* ── Right login card ── */}
      <div style={s.right}>
        <div style={s.card}>
          <img src={`${process.env.PUBLIC_URL}/starlims-logo.png`} alt="STARLIMS" style={s.logo} />

          {step === 'email' ? (
            <>
              <h1 style={s.heading}>Sign in</h1>
              <p style={s.sub}>Enter your work email to continue.</p>

              <label style={s.label}>Email address</label>
              <input
                style={s.input}
                type="email"
                placeholder="you@example.com"
                value={email}
                autoFocus
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleEmailSubmit()}
              />

              {email.trim() && (
                <div style={s.hint}>
                  <span style={{ ...s.hintDot, background: isStarLIMS ? BRAND_BLUE : BRAND_PURPLE }} />
                  {isStarLIMS
                    ? 'Sign in with StarLIMS SSO'
                    : 'A one-time code will be sent to this address'}
                </div>
              )}

              <button
                style={{ ...s.btn, opacity: (loading || !email.trim()) ? 0.5 : 1 }}
                onClick={handleEmailSubmit}
                disabled={loading || !email.trim()}
              >
                {loading ? 'Please wait…' : 'Continue'}
              </button>
            </>
          ) : (
            <>
              <h1 style={s.heading}>Check your email</h1>
              <p style={s.sub}>
                {sent
                  ? <> We sent a 6-digit code to <strong>{email}</strong>. It expires in 15 minutes.</>
                  : 'Enter your access code below.'}
              </p>

              <label style={s.label}>Access code</label>
              <input
                style={{ ...s.input, letterSpacing: '0.3em', fontSize: 24, textAlign: 'center', fontWeight: 500 }}
                type="text"
                inputMode="numeric"
                placeholder="000000"
                maxLength={6}
                value={code}
                autoFocus
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => e.key === 'Enter' && handleOtpSubmit()}
              />

              <button
                style={{ ...s.btn, opacity: (loading || code.length !== 6) ? 0.5 : 1 }}
                onClick={handleOtpSubmit}
                disabled={loading || code.length !== 6}
              >
                {loading ? 'Verifying…' : 'Sign in'}
              </button>

              <div style={s.links}>
                <button style={s.linkBtn} onClick={handleResend} disabled={loading}>
                  Resend code
                </button>
                <span style={{ color: '#ccc' }}>·</span>
                <button style={s.linkBtn} onClick={() => { setStep('email'); setCode(''); setError(''); }}>
                  Use a different email
                </button>
              </div>
            </>
          )}

          {error && <div style={s.error}>{error}</div>}

        </div>
      </div>
    </div>
  );
}

const BRAND_PURPLE = '#3D1A8E';
const BRAND_BLUE   = '#009BDD';   // the STARLIMS blue of the product pages

const s = {
  shell: {
    display: 'flex',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  left: {
    flex: '1 1 45%',
    background: `linear-gradient(145deg, ${BRAND_PURPLE} 0%, #2a1266 60%, #1a0a4a 100%)`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '48px 52px',
    minWidth: 0,
    position: 'relative',
    overflow: 'hidden',
  },
  leftInner: { maxWidth: 400, position: 'relative', zIndex: 1 },
  art: { width: '100%', maxHeight: '48vh', minHeight: 0, flex: '1 1 auto', margin: '12px 0', opacity: 0.96 },
  logoTile: {
    display: 'inline-block',
    background: '#fff',
    borderRadius: 12,
    padding: '14px 22px',
    marginBottom: 28,
    boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
  },
  logoTileImg: { display: 'block', width: 190, maxWidth: '100%', height: 'auto' },
  logo: {
    display: 'block',
    width: 200,
    maxWidth: '100%',
    height: 'auto',
    margin: '0 auto 24px',
  },
  leftFooter: {
    position: 'relative',
    zIndex: 1,
    color: 'rgba(255,255,255,0.35)',
    fontSize: 12,
  },
  right: {
    flex: '1 1 55%',
    background: '#f7f7f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 24px',
  },
  card: {
    background: '#fff',
    borderRadius: 14,
    boxShadow: '0 2px 24px rgba(0,0,0,0.07)',
    padding: '40px 44px 36px',
    width: '100%',
    maxWidth: 400,
  },
  heading: {
    fontSize: 22,
    fontWeight: 600,
    color: '#1a1a1a',
    margin: '0 0 6px',
  },
  sub: {
    fontSize: 14,
    color: '#666',
    margin: '0 0 24px',
    lineHeight: 1.5,
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 500,
    color: '#555',
    marginBottom: 6,
    letterSpacing: '0.02em',
  },
  input: {
    width: '100%',
    padding: '10px 13px',
    fontSize: 14,
    border: '1.5px solid #e0e0e0',
    borderRadius: 8,
    marginBottom: 14,
    boxSizing: 'border-box',
    color: '#1a1a1a',
    outline: 'none',
  },
  hint: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 12,
    color: '#666',
    marginBottom: 14,
    marginTop: -6,
  },
  hintDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
  },
  btn: {
    width: '100%',
    padding: '11px 0',
    background: BRAND_BLUE,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: 14,
    letterSpacing: '0.01em',
  },
  links: {
    display: 'flex',
    gap: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 4,
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: BRAND_BLUE,
    fontSize: 12,
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
    fontFamily: 'inherit',
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 6,
    color: '#991b1b',
    fontSize: 12,
    padding: '8px 12px',
    marginTop: 4,
  },
};
